import React, { useEffect, useMemo, useRef } from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useEditor, EditorContent } from '@tiptap/react';
import { createNotesEditorExtensions, serializeNotesDoc } from '../../utils/notesEditorExtensions';
import { normalizeNotesContentForEditor } from '../../utils/notesFormat';
import { NOTES_FORMAT_ACTIONS, NOTES_TAB_COLORS, runNotesFormatCommand } from '../../utils/notesCommands';

function NotesFormatToolbar({ theme, styles, editor, onChange, tour = false, pal }) {
  const ink = tour ? (pal?.ink || theme.text.primary) : theme.text.primary;
  return (
    <View style={styles.toolbarWrap}>
      <View style={styles.toolbarRow}>
        {NOTES_FORMAT_ACTIONS.map((action) => (
          <TouchableOpacity
            key={action.id}
            style={styles.toolBtn}
            onPress={() => runNotesFormatCommand(editor, action, onChange)}
            accessibilityLabel={action.title || action.label}
          >
            {action.id === 'bold' ? (
              <Ionicons name="text" size={16} color={ink} />
            ) : action.id === 'bullet' ? (
              <Ionicons name="list-outline" size={16} color={ink} />
            ) : action.id === 'divider' ? (
              <Ionicons name="remove-outline" size={16} color={ink} />
            ) : (
              <Text style={styles.toolBtnText}>{action.label}</Text>
            )}
          </TouchableOpacity>
        ))}
        {NOTES_TAB_COLORS.map((c) => (
          <TouchableOpacity
            key={`notes-color-${c}`}
            style={[styles.colorDot, { backgroundColor: c }]}
            onPress={() => runNotesFormatCommand(editor, { id: 'color', hex: c }, onChange)}
            accessibilityLabel={`Color ${c}`}
          />
        ))}
      </View>
    </View>
  );
}

export function DayNotesEditor({
  value,
  onChange,
  placeholder = 'Start writing notes for this day…',
  theme,
  compact = false,
  fill = false,
  cellMode = false,
  inputMinHeight,
  instanceKey = '',
  variant = 'legacy',
  pal,
  isDarkMode = false,
}) {
  const lastHtmlRef = useRef('');
  const tour = variant === 'tour';
  const resolvedMinHeight = cellMode ? 44 : inputMinHeight ?? (fill ? 160 : compact ? 120 : 160);
  const styles = useMemo(
    () => createStyles(theme, compact, fill, cellMode, tour, pal),
    [theme, compact, fill, cellMode, tour, pal]
  );

  const extensions = useMemo(
    () => createNotesEditorExtensions({ placeholder }),
    [placeholder]
  );

  const editor = useEditor({
    extensions,
    content: '<p></p>',
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'vrc-day-notes-editor',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = serializeNotesDoc(ed);
      lastHtmlRef.current = html;
      onChange?.(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const next = normalizeNotesContentForEditor(value);
    if (next === lastHtmlRef.current) return;
    lastHtmlRef.current = next;
    editor.commands.setContent(next, false);
  }, [value, editor]);

  const editorStyle = useMemo(
    () =>
      cellMode
        ? {
            minHeight: resolvedMinHeight,
            maxHeight: 120,
            overflowY: 'auto',
            padding: 0,
            border: 'none',
            borderRadius: 0,
            backgroundColor: 'transparent',
            color: theme.input.text,
            fontSize: 11,
            lineHeight: 1.45,
            outline: 'none',
          }
        : tour
          ? {
              minHeight: resolvedMinHeight,
              padding: 0,
              border: 'none',
              borderRadius: 0,
              backgroundColor: 'transparent',
              color: pal?.ink || theme.input.text,
              fontSize: 15,
              lineHeight: 1.55,
              outline: 'none',
            }
          : {
            minHeight: resolvedMinHeight,
            padding: 12,
            borderRadius: 10,
            border: `1px solid ${theme.ui.border}`,
            backgroundColor: theme.input.background,
            color: theme.input.text,
            fontSize: 15,
            lineHeight: 1.55,
            outline: 'none',
          },
    [resolvedMinHeight, theme, cellMode, tour, pal]
  );

  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <View style={styles.wrap}>
      {!cellMode ? <NotesFormatToolbar theme={theme} styles={styles} editor={editor} onChange={onChange} tour={tour} pal={pal} /> : null}
      <div
        key={instanceKey ? `notes-shell-${instanceKey}` : undefined}
        className={`vrc-day-notes-editor-shell${cellMode ? ' vrc-day-notes-editor-shell--cell' : ''}`}
        style={editorStyle}
      >
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .vrc-day-notes-editor-shell .ProseMirror {
          min-height: inherit;
          outline: none;
        }
        .vrc-day-notes-editor {
          min-height: inherit;
          outline: none;
        }
        .ProseMirror p.is-empty:first-child::before {
          content: attr(data-placeholder);
          color: ${tour ? (pal?.faint || theme.input.placeholder) : theme.input.placeholder};
          pointer-events: none;
          float: left;
          height: 0;
        }
        .ProseMirror-focused {
          outline: none;
        }
        .vrc-day-notes-editor ul {
          margin: 0 0 8px 18px;
          padding: 0;
        }
        .vrc-day-notes-editor p {
          margin: 0 0 8px;
        }
        .vrc-day-notes-editor hr {
          border: none;
          border-top: 1px solid ${tour ? (pal?.line || theme.ui.border) : theme.ui.border};
          margin: 10px 0;
        }
        .vrc-day-notes-editor h2,
        .vrc-day-notes-editor h3 {
          margin: 10px 0 6px;
          font-weight: 800;
        }
        .vrc-day-notes-editor-shell--cell .vrc-day-notes-editor ul {
          margin: 0 0 4px 14px;
          padding: 0;
        }
        .vrc-day-notes-editor-shell--cell .vrc-day-notes-editor p {
          margin: 0 0 4px;
          font-size: 11px;
          line-height: 1.45;
        }
        .vrc-day-notes-editor-shell--cell .vrc-day-notes-editor h2,
        .vrc-day-notes-editor-shell--cell .vrc-day-notes-editor h3 {
          margin: 4px 0 2px;
          font-size: 12px;
          font-weight: 800;
        }
        .vrc-day-notes-editor-shell--cell .vrc-day-notes-editor hr {
          margin: 4px 0;
        }
        .vrc-day-notes-editor-shell--cell .ProseMirror p.is-empty:first-child::before {
          font-size: 11px;
        }
      `}</style>
    </View>
  );
}

function createStyles(theme, compact, fill, cellMode = false, tour = false, pal) {
  return StyleSheet.create({
    wrap: {
      gap: cellMode ? 0 : tour ? 14 : compact ? 8 : 10,
      width: '100%',
      minWidth: 0,
      ...(fill ? { flex: 1, minHeight: 0 } : {}),
    },
    toolbarWrap: { gap: 8 },
    toolbarSection: { gap: 4 },
    toolbarLabel: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.text.tertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    toolbarRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      alignItems: 'center',
    },
    toolBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: tour ? 12 : 8,
      paddingVertical: tour ? 7 : 5,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: tour ? pal?.line || theme.ui.border : theme.ui.border,
      backgroundColor: tour ? 'transparent' : theme.background.secondary,
    },
    toolBtnText: {
      fontSize: tour ? 12.5 : 11,
      fontWeight: '600',
      color: tour ? pal?.ink || theme.text.primary : theme.text.primary,
    },
    colorDot: {
      width: tour ? 18 : 22,
      height: tour ? 18 : 22,
      borderRadius: tour ? 9 : 11,
      borderWidth: 1,
      borderColor: pal?.line || 'rgba(0,0,0,0.15)',
    },
  });
}

export default DayNotesEditor;
